import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Plus,
  Pencil,
  Trash2,
  Folder,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const COLORS = ['#ffd108', '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#a855f7', '#14b8a6', '#ec4899'];

const ScheduleFoldersTab = ({ api, equipmentTypes = [] }) => {
  const [folders, setFolders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', color: '#ffd108', equipment_type_ids: [] });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [assetTypeOptions, setAssetTypeOptions] = useState([]);

  const fetchFolders = async () => {
    try {
      const res = await api.get('/schedule-folders');
      setFolders(res.data || []);
    } catch {
      toast.error('Failed to load schedule folders');
    }
  };

  const fetchAssetTypeOptions = async () => {
    try {
      const res = await api.get('/schedule-folders/asset-types');
      setAssetTypeOptions(res.data || []);
    } catch {
      // fall back to the catalog prop if the endpoint is unavailable
      setAssetTypeOptions(equipmentTypes);
    }
  };

  useEffect(() => { fetchFolders(); fetchAssetTypeOptions(); }, [api]);

  // Resolve a stored value to a display name. New folders store the type NAME
  // directly; legacy folders may store a catalog id — resolve that via the prop.
  const typeName = (idOrName) =>
    equipmentTypes.find(t => t.id === idOrName)?.name || idOrName;

  // Normalise a folder's stored equipment_type_ids to NAMES so both legacy
  // (catalog-id) and new (name) folders show the right options selected.
  const toNames = (ids = []) => ids.map(v => typeName(v));

  const openDialog = (folder = null) => {
    if (folder) {
      setEditing(folder);
      setForm({ name: folder.name, color: folder.color || '#ffd108', equipment_type_ids: toNames(folder.equipment_type_ids || []) });
    } else {
      setEditing(null);
      setForm({ name: '', color: '#ffd108', equipment_type_ids: [] });
    }
    setDialogOpen(true);
  };

  const toggleType = (id) => {
    setForm(prev => ({
      ...prev,
      equipment_type_ids: prev.equipment_type_ids.includes(id)
        ? prev.equipment_type_ids.filter(t => t !== id)
        : [...prev.equipment_type_ids, id],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Folder name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/schedule-folders/${editing.id}`, form);
        toast.success('Folder updated');
      } else {
        await api.post('/schedule-folders', form);
        toast.success('Folder created');
      }
      setDialogOpen(false);
      fetchFolders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save folder');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (folder) => {
    setDeleteTarget(folder);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/schedule-folders/${deleteTarget.id}`);
      toast.success('Folder deleted');
      fetchFolders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete folder');
    } finally {
      setDeleteTarget(null);
    }
  };

  const move = async (idx, dir) => {
    const next = [...folders];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setFolders(next);
    try {
      await api.put('/schedule-folders/reorder', { ordered_ids: next.map(f => f.id) });
    } catch { /* silent; UI already reflects intent */ }
  };

  return (
    <>
      <Card className="card-industrial">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="font-heading">Schedule Folders</CardTitle>
              <CardDescription>
                Group your daily schedule by asset type. Create folders and assign which asset types appear inside — a type can be reused across folders. Assets whose type isn't in any folder fall into "Ungrouped".
              </CardDescription>
            </div>
            <Button className="btn-volt" onClick={() => openDialog()} data-testid="add-schedule-folder-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Folder
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {folders.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground border border-dashed border-zinc-800 rounded-sm">
              No schedule folders yet. Add your first one to enable the Folders view on the schedule.
            </div>
          ) : (
            <div className="space-y-2">
              {folders.map((folder, idx) => (
                <div
                  key={folder.id}
                  className="flex items-center gap-3 rounded-sm border border-zinc-800 bg-zinc-900/50 p-3"
                  data-testid={`schedule-folder-row-${folder.id}`}
                >
                  <div className="flex flex-col">
                    <button className="text-zinc-500 hover:text-zinc-200 leading-none" onClick={() => move(idx, -1)} disabled={idx === 0} title="Move up">▲</button>
                    <button className="text-zinc-500 hover:text-zinc-200 leading-none" onClick={() => move(idx, 1)} disabled={idx === folders.length - 1} title="Move down">▼</button>
                  </div>
                  <Folder className="h-5 w-5 shrink-0" style={{ color: folder.color || '#ffd108' }} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-zinc-100">{folder.name}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(folder.equipment_type_ids || []).length === 0 ? (
                        <span className="text-xs text-muted-foreground italic">No asset types assigned</span>
                      ) : (
                        folder.equipment_type_ids.map(tid => (
                          <Badge key={tid} variant="secondary" className="text-[10px]">{typeName(tid)}</Badge>
                        ))
                      )}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDialog(folder)} data-testid={`edit-schedule-folder-${folder.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => handleDelete(folder)} data-testid={`delete-schedule-folder-${folder.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="schedule-folder-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Edit Folder' : 'Add Folder'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="sf-name">Folder Name <span className="text-red-500">*</span></Label>
              <Input
                id="sf-name"
                data-testid="schedule-folder-name-input"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., 45T Boom Trucks"
              />
            </div>
            <div>
              <Label>Colour</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, color: c }))}
                    className={`h-7 w-7 rounded-sm border-2 transition-transform ${form.color === c ? 'border-white scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: c }}
                    data-testid={`schedule-folder-color-${c}`}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label>Asset Types <span className="text-muted-foreground text-xs">({form.equipment_type_ids.length} selected)</span></Label>
              <p className="text-xs text-muted-foreground mt-1">Shows the asset types actually in use plus your catalog types.</p>
              <div className="mt-2 max-h-56 overflow-y-auto border border-zinc-800 rounded-sm divide-y divide-zinc-800">
                {assetTypeOptions.length === 0 ? (
                  <p className="p-3 text-sm text-muted-foreground">No asset types found. Add assets or asset types first.</p>
                ) : (
                  assetTypeOptions.map(t => (
                    <label key={t.id} className="flex items-center gap-3 p-2.5 hover:bg-zinc-900 cursor-pointer" data-testid={`schedule-folder-type-${t.id}`}>
                      <Checkbox checked={form.equipment_type_ids.includes(t.id)} onCheckedChange={() => toggleType(t.id)} />
                      <span className="text-sm flex-1">{t.name}</span>
                      {typeof t.asset_count === 'number' && (
                        t.asset_count > 0
                          ? <Badge variant="secondary" className="text-[10px]">{t.asset_count} asset{t.asset_count !== 1 ? 's' : ''}</Badge>
                          : <span className="text-[10px] text-muted-foreground italic">no assets</span>
                      )}
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="btn-volt" onClick={handleSave} disabled={saving} data-testid="schedule-folder-save-btn">
              {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Folder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-schedule-folder-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Delete Schedule Folder
            </AlertDialogTitle>
            <AlertDialogDescription>
              Delete folder "{deleteTarget?.name}"? Assets stay untouched — only the grouping is removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-schedule-folder-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-schedule-folder-button">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ScheduleFoldersTab;
